import { fetchWithAuth, apiUrl } from '@/modules/shared/lib/apiBase'
import type { EventTypeEnum, TrackEventItem } from '@/generated/user-events-tracking/types'

const MAX_BUFFER = 50
const HARD_LIMIT = 200
const FLUSH_INTERVAL_MS = 5_000

function truncateIsoToSeconds(iso: string): string {
  return iso.replace(/\.\d{3}Z$/, 'Z')
}

function safeNowIsoSeconds(): string {
  return truncateIsoToSeconds(new Date().toISOString())
}

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return `sess_${crypto.randomUUID().slice(0, 12)}`

  const key = 'y_session_id'
  const existing = sessionStorage.getItem(key)
  if (existing) return existing

  const next = `sess_${crypto.randomUUID().slice(0, 12)}`
  sessionStorage.setItem(key, next)
  return next
}

type DeviceType = 'mobile' | 'tablet' | 'desktop'

function resolveDeviceType(ua: string): DeviceType {
  const raw = ua.toLowerCase()
  if (/ipad|tablet|kindle|silk/.test(raw)) return 'tablet'
  if (/mobi|android|iphone|ipod/.test(raw)) return 'mobile'
  return 'desktop'
}

function getDeviceTypeCached(): DeviceType {
  if (typeof navigator === 'undefined') return 'desktop'
  return resolveDeviceType(navigator.userAgent || '')
}

let initialized = false

class EventTracker {
  private buffer: TrackEventItem[] = []
  private flushing = false
  private flushTimer: ReturnType<typeof setInterval> | null = null

  private sessionId = getOrCreateSessionId()
  private deviceType: DeviceType = getDeviceTypeCached()
  private sessionStartedAtMs = typeof performance !== 'undefined' ? performance.now() : Date.now()
  private totalEventsTracked = 0
  private sessionEndSent = false

  init(): void {
    if (initialized) return
    initialized = true

    this.flushTimer = window.setInterval(() => {
      void this.flush()
    }, FLUSH_INTERVAL_MS)

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('beforeunload', this.handleVisibilityChange)

    this.track('session_start', {
      session_id: this.sessionId,
      device_type: this.deviceType,
    })
  }

  track(eventType: EventTypeEnum, eventData: Record<string, unknown> = {}): void {
    this.totalEventsTracked++
    const eventId = `evt_${crypto.randomUUID().slice(0, 12)}`
    const occurredAt = safeNowIsoSeconds()

    const eventItem: TrackEventItem = {
      event_id: eventId,
      event_type: eventType,
      occurred_at: occurredAt,
      session_id: this.sessionId,
      device_type: this.deviceType,
      event_data: eventData,
    }

    this.buffer.push(eventItem)

    if (this.buffer.length > HARD_LIMIT) {
      this.buffer.splice(0, this.buffer.length - HARD_LIMIT)
    }

    if (this.buffer.length >= MAX_BUFFER) {
      void this.flush()
    }
  }

  async flush(): Promise<void> {
    if (this.flushing) return
    if (this.buffer.length === 0) return

    this.flushing = true
    const batch = this.buffer.splice(0)
    const batchId = `batch_${crypto.randomUUID().slice(0, 12)}`

    try {
      const response = await fetchWithAuth(apiUrl('/api/v1/events/track'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          batch_id: batchId,
          events: batch,
        }),
      })

      if (!response.ok) {
        this.buffer.unshift(...batch)
        if (this.buffer.length > HARD_LIMIT) {
          this.buffer.splice(0, this.buffer.length - HARD_LIMIT)
        }
      }
    } catch {
      // Re-enqueue failed batch at front for retry on next flush cycle.
      // If the same batch keeps failing, HARD_LIMIT trim drops oldest (front) events,
      // which self-heals by eventually discarding the stuck batch.
      this.buffer.unshift(...batch)
      if (this.buffer.length > HARD_LIMIT) {
        this.buffer.splice(0, this.buffer.length - HARD_LIMIT)
      }
    } finally {
      this.flushing = false
    }
  }

  private handleVisibilityChange = (): void => {
    // User returned to tab — reset so next departure sends session_end
    if (!document.hidden) {
      if (this.sessionEndSent) {
        this.sessionEndSent = false
        this.sessionStartedAtMs = typeof performance !== 'undefined' ? performance.now() : Date.now()
      }
      return
    }
    if (this.sessionEndSent) return
    this.sessionEndSent = true

    const durationMs = Math.max(
      0,
      Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) -
          this.sessionStartedAtMs
      )
    )

    const payload = {
      batch_id: `batch_${crypto.randomUUID().slice(0, 12)}`,
      events: [
        {
          event_id: `evt_${crypto.randomUUID().slice(0, 12)}`,
          event_type: 'session_end' as EventTypeEnum,
          occurred_at: safeNowIsoSeconds(),
          session_id: this.sessionId,
          device_type: this.deviceType,
          event_data: {
            duration_ms: durationMs,
            events_count: this.totalEventsTracked,
          },
        } satisfies TrackEventItem,
      ],
    }

    try {
      const url = apiUrl('/api/v1/events/track')
      fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* best-effort */ })
    } catch {
      // ignore
    }
  }
}

const tracker = new EventTracker()

export function initTracker(): void {
  if (typeof window === 'undefined') return
  tracker.init()
}

export function track(eventType: EventTypeEnum, eventData: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return
  tracker.track(eventType, eventData)
}
